import React, { useState, createContext, useContext } from 'react';

export interface AccordionProps {
  children: React.ReactNode;
  className?: string;
}

export interface AccordionItemProps {
  title?: React.ReactNode;
  children: React.ReactNode;
  isOpen?: boolean;
  onToggle?: () => void;
  className?: string;
}

export interface UncontrolledAccordionProps {
  defaultOpen?: string;
  open?: string;
  toggle?: (id: string) => void;
  children: React.ReactNode;
  className?: string;
}

export interface AccordionHeaderProps {
  targetId: string;
  children: React.ReactNode;
  className?: string;
}

export interface AccordionBodyProps {
  accordionId: string;
  children: React.ReactNode;
  className?: string;
}

interface AccordionContextType {
  open: string;
  toggle: (id: string) => void;
}

const AccordionContext = createContext<AccordionContextType>({
  open: '',
  toggle: () => {},
});

export const Accordion: React.FC<AccordionProps> = ({ children, className = '' }) => {
  return (
    <div className={`accordion ${className || ''}`}>
      {children}
    </div>
  );
};

export const UncontrolledAccordion: React.FC<UncontrolledAccordionProps> = ({
  defaultOpen = '',
  open: controlledOpen,
  toggle: controlledToggle,
  children,
  className = ''
}) => {
  const [open, setOpen] = useState<string>(defaultOpen);

  const toggle = (id: string) => {
    if (controlledToggle) {
      controlledToggle(id);
    } else {
      setOpen(prev => (prev === id ? '' : id));
    }
  };

  const currentOpen = controlledOpen !== undefined ? controlledOpen : open;

  return (
    <AccordionContext.Provider value={{ open: currentOpen, toggle }}>
      <div className={`accordion ${className || ''}`}>
        {children}
      </div>
    </AccordionContext.Provider>
  );
};

export const AccordionHeader: React.FC<AccordionHeaderProps> = ({
  targetId,
  children,
  className = ''
}) => {
  const { open, toggle } = useContext(AccordionContext);
  const isOpen = open === targetId;

  return (
    <button
      type="button"
      onClick={() => toggle(targetId)}
      className={`accordion-header w-full text-left px-4 py-3 flex items-center justify-between bg-white hover:bg-[var(--color-gray-50)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] transition ${className || ''}`}
      aria-expanded={isOpen}
    >
      <span className="font-medium">{children}</span>
      <span className={`transform transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}>
        ▼
      </span>
    </button>
  );
};

export const AccordionBody: React.FC<AccordionBodyProps> = ({
  accordionId,
  children,
  className = ''
}) => {
  const { open } = useContext(AccordionContext);
  const isOpen = open === accordionId;

  return (
    <div
      className={`accordion-body overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'} ${className || ''}`}
    >
      <div className="p-4">
        {children}
      </div>
    </div>
  );
};

export const AccordionItem: React.FC<AccordionItemProps> = ({
  title,
  children,
  isOpen: controlledIsOpen,
  onToggle,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(controlledIsOpen || false);

  const handleToggle = () => {
    const newState = !isOpen;
    setIsOpen(newState);
    if (onToggle) {
      onToggle();
    }
  };

  if (!title) {
    return (
      <div className={`accordion-item border border-[var(--color-gray-300)] rounded mb-2 ${className || ''}`}>
        {children}
      </div>
    );
  }

  return (
    <div className={`accordion-item border border-[var(--color-gray-300)] rounded mb-2 ${className || ''}`}>
      <button
        onClick={handleToggle}
        className="w-full text-left px-4 py-3 flex items-center justify-between bg-white hover:bg-[var(--color-gray-50)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] transition"
        aria-expanded={isOpen}
      >
        <span className="font-medium">{title}</span>
        <span className={`transform transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}>
          ▼
        </span>
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}
      >
        <div className="p-4">
          {children}
        </div>
      </div>
    </div>
  );
};
